"use client";

import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";

import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

interface Step3Props {
  onNext: (password: string) => void;
}

const passwordSchema = z
  .object({
    password: z.string().regex(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/, {
      message: "비밀번호는 영문, 숫자, 특수문자를 포함해 8자 이상 입력해 주세요",
    }),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다.",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof passwordSchema>;

export default function Step3NewPasswordForm({ onNext }: Readonly<Step3Props>) {
  const {
    register,
    formState: { errors },
    watch,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(passwordSchema),
    mode: "onChange",
  });

  const passwordValue = watch("password");
  const confirmValue = watch("confirmPassword");

  // 비밀번호 visible
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmitStep = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    onNext(passwordValue);
  };

  return (
    <FieldSet>
      <FieldGroup>
        <Field>
          <FieldLabel className="text-base">새 비밀번호</FieldLabel>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="영문, 숫자, 특수문자 포함 8자 이상"
              aria-invalid={!!errors.password}
              {...register("password")}
              onChange={e => {
                register("password").onChange(e);
                trigger("password");
              }}
              className="h-11 pr-[54px] text-base"
            />
            <Button
              type="button"
              variant="ghost"
              aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              className="absolute top-1/2 right-1.5 -translate-y-1/2 px-3"
              onClick={() => setShowPassword(prev => !prev)}
            >
              {showPassword ? <Eye /> : <EyeOff />}
            </Button>
          </div>
          {errors.password && (
            <p className="text-error-600 pl-3 text-sm">{errors.password.message}</p>
          )}
        </Field>

        <Field>
          <FieldLabel className="text-base">새 비밀번호 확인</FieldLabel>
          <div className="relative">
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="한번 더 입력해 주세요"
              aria-invalid={!!errors.confirmPassword}
              {...register("confirmPassword")}
              onChange={e => {
                register("confirmPassword").onChange(e);
                trigger("confirmPassword");
              }}
              className="h-11 pr-[54px] text-base"
            />
            <Button
              type="button"
              variant="ghost"
              aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              className="absolute top-1/2 right-1.5 -translate-y-1/2 px-3"
              onClick={() => setShowConfirmPassword(prev => !prev)}
            >
              {showConfirmPassword ? <Eye /> : <EyeOff />}
            </Button>
          </div>
          {errors.confirmPassword && (
            <p className="text-error-600 pl-3 text-sm">{errors.confirmPassword.message}</p>
          )}
        </Field>

        <Field className="pt-2">
          <Button
            type="button"
            onClick={handleSubmitStep}
            disabled={
              !passwordValue || !confirmValue || !!errors.password || !!errors.confirmPassword
            }
            className="text-md h-11"
          >
            변경하기
          </Button>
        </Field>
      </FieldGroup>
    </FieldSet>
  );
}
